// Libraries
import { Box, Collapse, Flex, Input, InputGroup, InputRightElement, Text } from '@chakra-ui/react';
import { forwardRef, useImperativeHandle } from 'react';
// Components
import { CalendarIcon } from '@atoms/Icons';
import { useDatePicker } from '@hooks/index';
import { CalendarLayout } from '@molecules/index';
// Utils
import text from '@translations/en.json';
// Types
import { DatePickerProps, ResetDateHandle } from './DatePicker.types';

/**
 * DatePicker component represents a date picker.
 *
 * @component
 * @param {DatePickerProps} props - The component props.
 * @param {number[]} props.availableYears - The available years in the date picker.
 * @param {number} props.weekStartsOn - The starting day of the week (0 - Sunday, 1 - Monday, etc.).
 * @param {number} props.numberOfMonths - The number of months to display in the date picker.
 * @param {Date} props.minDate - The minimum selectable date in the date picker.
 * @param {Date} props.maxDate - The maximum selectable date in the date picker.
 * @param {Date[]} props.selected - The initially selected dates in the date picker.
 * @param {Function} props.onSelected - The function to handle selected dates.
 * @param {Date} props.defaultDate - The default date for the date picker.
 * @param {string} props.queryParam - The query parameter to use for the date picker's selected date.
 * @param {string} props.label - The label for the date picker.
 * @param {React.Ref<ResetDateHandle>} ref - The ref to expose the resetDate function.
 * @returns {ReactElement} DatePicker component.
 */
export const DatePicker = forwardRef<ResetDateHandle, DatePickerProps>((props, ref) => {
  // Destructure the props
  const {
    availableYears,
    weekStartsOn,
    numberOfMonths,
    minDate,
    maxDate,
    startDate,
    endDate,
    selected: initialDates,
    onSelected,
    shortcutDate,
    queryParam,
    label,
    shortcutDateLabel,
    initialDate,
    onClick,
    ...rest
  } = props;

  const { border, pickerParams, toggle, togglePicker, value, isOpen, boxShadow } = useDatePicker({
    availableYears,
    weekStartsOn,
    numberOfMonths,
    startDate,
    endDate,
    selected: initialDates,
    onSelected,
    shortcutDate,
    queryParam,
    shortcutDateLabel,
  });

  // Expose the resetDate function via the ref for external use
  useImperativeHandle(
    ref,
    () => ({
      resetDate: () => {
        if (initialDate) {
          toggle([initialDate], onSelected, true);
        }
      },
      togglePicker: (state) => {
        togglePicker(state);
      },
    }),
    /**
     * TODO: We need to search how works the dependencies in useImperativeHandle
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Flex borderBottom='section' direction='column' w={90}>
      <Box p={4}>
        {/* Label for the date picker */}
        <Text color='grey.100' fontSize='s' mb={1}>
          {label}
        </Text>
        {/* Input group container */}
        <InputGroup
          cursor='pointer'
          w={48}
          border={border}
          boxShadow={boxShadow}
          bg='white'
          borderRadius='md'
          onClick={onClick}
        >
          {/* Input element */}
          <Input
            ref={ref}
            h={8}
            border='none'
            placeholder={text.select_a_date}
            fontSize='m'
            color='darkGray.100'
            value={value}
            cursor='inherit'
            {...rest}
            readOnly
          />
          {/* Calendar icon */}
          <InputRightElement h={8}>
            <CalendarIcon color='tealishBlue' />
          </InputRightElement>
        </InputGroup>
      </Box>
      {/* Date picker calendar layout */}
      <Collapse in={isOpen} animateOpacity>
        <CalendarLayout {...pickerParams} minDate={minDate} maxDate={maxDate} />
      </Collapse>
    </Flex>
  );
});

DatePicker.displayName = 'DatePicker';
